<template>
  <el-container style="display: flex" v-loading="isFetchingMaterial || (!pdfLoaded && !htmlLoaded)">
    <perfect-scrollbar
      :class="['pdf-wrapper zoom-active', { 'pdf-html': showPDFHtml, 'material-control': showMaterialControl }]"
      v-dragscroll="true"
      :style="{ height: materialHeight }"
      :ref="scrollbar"
      @scroll="scrollMaterial"
    >
      <pinch-zoom
        :class="[
          'pinch-wrapper',
          { 'pc-view': isPCView },
          { 'html-pinch-wrapper': showPDFHtml },
          `level-${htmlLevel}`
        ]"
        :style="styleWithHeight"
        :disabled="!isEnabledPinchZoom"
        :wheel="false"
        limitZoom="3"
        ref="pinchZoom"
      >
        <div v-if="!showPDFHtml && enablePdf && !isFetchingMaterial">
          <!-- Show multi-page PDF per lesson -->
          <PDFMaterialMultiPage
            v-if="scrollableMultiplePages"
            :pdf-url="pdfUrl"
            :style-with-height="styleWithHeight"
            :key="'multipage-' + pdfComponentKey"
            @page-loaded="onPdfLoaded"
            @on-loaded="pdfLinkInNewTab"
          />

          <!-- Show single-page pdf per lesson [Teacher Material]-->
          <PDFMaterialSinglePage
            v-else
            :pdf-url="trainerPdfUrl || pdfUrl"
            :current-page="pdfCurrentPage"
            :style-with-height="styleWithHeight"
            :class="[
              pdfLoaded ? 'loaded' : 'loading',
              { 'macos-safari': isMobileMacOsSafari },
              { 'ios-safari': isMobileIosSafari }
            ]"
            :key="'singlepage-' + pdfComponentKey"
            @page-loaded="onPdfLoaded"
            @on-loaded="pdfLinkInNewTab"
          />
        </div>
        <PDFHtmlMaterial
          v-else
          :isMobileWidth="isMobileWidth"
          @htmlLoaded="(state) => (htmlLoaded = state)"
        />
      </pinch-zoom>
    </perfect-scrollbar>

    <div :class="['material-zoom', { fullscreen: isMaterialFullScreen }]">
      <div class="material-fullscreen">
        <el-button @click="fullScreenMaterial" style="position: relative; z-index: 10" circle>
          <img :src="zoomIcon" />
        </el-button>
      </div>
    </div>
    <div v-show="pdfLoaded || (htmlLoaded && isCorpLesson)">
      <Notification
        v-if="isStudent && showLimitedFunctionalityNotif"
        :message="$t('pdfNotification')"
      />
    </div>
  </el-container>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import { dragscroll } from 'vue-dragscroll';

import PinchZoom from 'vue-pinch-zoom';
import UAParser from 'ua-parser-js';
import PDFHtmlMaterial from '@/components/Lesson/Material/PDFHtmlMaterial';
import PDFMaterialMultiPage from '@/components/Lesson/Material/PDFMaterialMultiPage';
import PDFMaterialSinglePage from '@/components/Lesson/Material/PDFMaterialSinglePage';
import Notification from '@/components/Lesson/Material/Notification';

const HTML_MATERIAL_MIN_WIDTH = 680;

export default {
  directives: { dragscroll },
  components: {
    PinchZoom,
    PDFHtmlMaterial,
    Notification,
    PDFMaterialMultiPage,
    PDFMaterialSinglePage
  },
  data() {
    return {
      pdfCurrentPage: 1,
      windowWidth: window.innerWidth,
      initializePdf: false,
      pdfLoaded: 0,
      pdfInitialSize: {
        height: 0,
        width: 0
      },
      pdfComponentKey: 0, // use to rerender component
      scrollbar: 'pdf-scrollbar',
      isPCView: false,
      isMobileMacOsSafari: false,
      isMobileIosSafari: false,
      isToEnablePdf: false,
      htmlLoaded: false,
      trainerPdfUrl: null
    };
  },
  computed: {
    ...mapState(['lessonId']),
    ...mapState('material', [
      'pdfUrl',
      'pdfZoom',
      'pdfHeight',
      'currentLesson',
      'showMaterialControl',
      'showMaterial',
      'showPDFHtml',
      'pdfHtmlZoom',
      'isFetchingMaterial',
      'isLevel0',
      'showPDFMaterial'
    ]),
    ...mapState('socketio', ['socket', 'scrollPoint']),
    ...mapState('layout', [
      'isMaterialFullScreen',
      'materialSwitch',
      'layoutStyle',
      'sliderLayout',
      'sliderPdfWidth',
      'isIpadDevice',
      'materialViewportHeight'
    ]),
    ...mapState('htmlMaterial', ['htmlLevel']),
    ...mapGetters('layout', [
      'isDefaultLayout',
      'isMaterialLayout',
      'isVideoScreenLayout',
      'isSliderInLayout2',
      'isTrainerLayout1View'
    ]),
    ...mapGetters([
      'isTrainer',
      'isStudent',
      'isAssessmentLesson',
      'isCorpLesson'
    ]),

    zoomIcon() {
      const icon = this.isMaterialFullScreen ? 'text_zoomout_sp@2x.png' : 'text_zoom_sp@2x.png';
      return require(`@/assets/img/${icon}`);
    },

    /**
     * Set the pdf as scrollable if pdf has multiple pages when user is student
     */
    scrollableMultiplePages() {
      return (this.isStudent && !this.isAssessmentLesson) || this.isTrainerLayout1View;
    },

    /**
     * Enable pdf when view is switch to `material` (SP) to fix problem of pdf not rendering on iphone/android on initial load
     */
    enablePdf() {
      const ua = new UAParser();
      const osName = ua.getOS().name;
      const device = ua.getDevice().model;
      let isSPDevice = false;

      if (
        ((osName === 'iOS' || this.isIpadWithMacOsUserAgent()) && device != 'iPad') ||
        osName === 'Android' || this.isMobileMacOsSafari
      ) {
        isSPDevice = true;
      }

      // Ipad mini 5 gen special case
      if (this.isIpadMini() && this.isPortraitOrientation()) {
        isSPDevice = true;
      }

      if (this.isAssessmentLesson && this.isMobileWidth) {
        isSPDevice = true;
      }

      const result = isSPDevice ? this.initializePdf : true;

      this.isToEnableDisplayOfPdf(true);
      this.setShowMaterial(result);
      return result;
    },

    /**
     * if current width less than 1600 include height in getting the new style object
     * Set min-width on HTML materials excluding the iPad (has a font-size reduce functionality)
     * Min supported screen width: 1440
     */
    styleWithHeight() {
      if (!this.pdfLoaded && !this.htmlLoaded) return;
      if (this.showPDFHtml && !this.isMobileWidth) {
        const materialWrapper = (window.innerWidth / 2 - 36) * this.pdfHtmlZoom;
        const minWidthFloor = HTML_MATERIAL_MIN_WIDTH * Math.min(this.pdfHtmlZoom, 1);
        const materialWidth = this.isIpadDevice
          ? materialWrapper
          : Math.max(materialWrapper, minWidthFloor);
        return { width: materialWidth + 'px', minWidth: materialWidth + 'px' };
      }
      return { width: this.sliderPdfWidth || this.pdfZoom + '%' };
    },

    /**
     * Get the difference of header and material selection's height to the window height
     *
     * Uses materialViewportHeight (not the live viewportHeight) because this
     * mobile branch subtracts the video column's height a second time on
     * top of the base height — if that base is already shrunk by an
     * on-screen keyboard, the result collapses to a sliver. See
     * .kiro/specs/lesson-room-viewport-keyboard/design.md.
     */
    materialHeight() {
      if (!this.pdfLoaded && !this.htmlLoaded) return;
      const initHeight = this.materialViewportHeight - this.otherElementsHeight;
      const videoColumn = document.querySelector('#video-column');
      if (this.isMobileWidth && videoColumn) {
        const isMobile = initHeight - videoColumn.offsetHeight - 16;
        return this.isMaterialFullScreen ? initHeight + 72 + 'px' : isMobile + 'px';
      }
      return initHeight + 'px';
    },

    otherElementsHeight() {
      const headerAndMaterialInfo = 132 + this.ipadHeightOffset;
      if (!this.showMaterialControl || this.isMobileWidth) {
        return headerAndMaterialInfo;
      }
      const materialControl = !this.isLayoutAdjustedToLayout2 ? 160 : 115;
      return materialControl + headerAndMaterialInfo;
    },

    /**
     * Extra height (px) iPad Safari needs subtracted beyond other devices.
     *
     * iPad Safari's `visualViewport.height` includes area behind browser
     * chrome that content cannot actually render into:
     * - 63px: URL/address bar area (derived from the `.iPad .content_3
     *   .chatbox` CSS precedent in main.css: 226px vs 163px)
     * - 44px: Tab bar (the Safari tab strip showing open tabs)
     *
     * Without this offset, `otherElementsHeight` under-subtracts on iPad,
     * so `materialHeight` computes taller than the actually visible area
     * and the material panel's bottom gets clipped.
     */
    ipadHeightOffset() {
      return this.isIpadDevice ? 107 : 0;
    },

    isLayoutAdjustedToLayout2() {
      return this.sliderLayout ? this.isSliderInLayout2 : this.isMaterialLayout;
    },

    isMobileWidth() {
      return this.windowWidth < 960;
    },

    /**
     * Disable pinch zoom on pc device
     */
    isEnabledPinchZoom() {
      return this.isMobileWidth || this.isIpadDevice;
    },

    isMaterialAssessmentOrLevel0() {
      return this.isAssessmentLesson || this.isLevel0;
    },

    showLimitedFunctionalityNotif() {
      return !JSON.parse(process.env.VUE_APP_DISPLAY_HTML) || this.isCorpLesson;
    },

    shouldDelayAssessmentPdfLoading() {
      return this.isMobileWidth && this.materialSwitch !== 'material'
    }
  },
  watch: {
    pdfUrl: {
      handler: function () {
        // set user's view to top after change of pdf
        this.$refs['pdf-scrollbar'].$el.scrollTop = 0;
        this.$refs['pdf-scrollbar'].$el.scrollLeft = 0;

        this.pdfLoaded = 0;
        this.pdfInitialSize.height = 0;
        this.ifIosLevel0();
        this.loadMaterialInitialWidth();
        this.resetPdfZoomLevel0();

        if (this.isMaterialLayout) {
          this.trainerPdfUrl = this.pdfUrl;
        }
      }
    },
    currentLesson: {
      handler: function () {
        if (this.isTrainer && this.isMaterialLayout) {
          this.pdfInitialSize.height = 0;
        }

        if (this.isAssessmentLesson) {
          this.initializePdf = !this.shouldDelayAssessmentPdfLoading;
        }

        this.setPdfCurrentLesson();
        if (!this.isAssessmentLesson) {
          this.resetPdfZoomLevel0();
          this.getPDFRatioSize();
        }
      }
    },
    materialSwitch(view) {
      if (view === 'material') {
        this.initializePdf = true;
        this.removeAutoCanvasResize();
      }
    },
    pdfZoom() {
      this.removeAutoCanvasResize();
    },
    layoutStyle() {
      this.pdfInitialSize.height = 0;
      if (!this.isVideoScreenLayout) {
        this.getPDFRatioSize();
      }

      if (this.isTrainer) {
        this.setPdfCurrentLesson();
      }
    },
    // pinned width cleared without a layout change (e.g. current layout re-selected): refit the canvas height
    sliderPdfWidth(width) {
      if (!width) {
        this.pdfInitialSize.height = 0;
        this.removeAutoCanvasResize();
      }
    },
    scrollPoint: {
      handler(newVal) {
        if (newVal) {
          // set to pinch out
          const pinchZoom = this.$refs.pinchZoom;
          if (pinchZoom !== undefined && pinchZoom.ivyPinch) {
            if (pinchZoom.isTouchScreen && pinchZoom.ivyPinch.scale > 1) {
              pinchZoom.toggleZoom();
            }
          }

          const pdfCanvasHeight = document.querySelector('.pdf-wrapper canvas').style.height;
          const percentageHeight = parseInt(pdfCanvasHeight, 10) * newVal.x;
          this.$refs['pdf-scrollbar'].$el.scrollTop = percentageHeight;
          this.$refs['pdf-scrollbar'].$el.scrollLeft = newVal.y;
        }
      },
      deep: true
    },
    isToEnablePdf: {
      handler(pdfState) {
        if (pdfState) {
          setTimeout(this.setInitializePdfForIpad(), 5000);
        }
      },
      deep: true
    },
    scrollableMultiplePages() {
      this.pdfLoaded = 0;
    }
  },
  created() {
    this.ifIosLevel0();
  },
  mounted() {
    if (this.isTrainer) {
      this.trainerPdfUrl = this.pdfUrl;
    }
    window.addEventListener('resize', () => {
      const wasMobile = this.windowWidth < 960;
      // Height is sourced from the shared layout.viewportHeight (visible
      // viewport); only width is tracked here for the breakpoint logic.
      this.windowWidth = window.innerWidth;
      const isMobile = this.windowWidth < 960;

      // Force re-render PDF when crossing the mobile/desktop breakpoint
      // to prevent squished display from stale canvas dimensions
      if (wasMobile !== isMobile) {
        this.pdfLoaded = 0;
        this.pdfInitialSize.height = 0;
        this.pdfInitialSize.width = 0;
        this.pdfComponentKey++;
      } else {
        this.getPDFRatioSize();
      }

      if (this.isLevel0 || this.isAssessmentLesson) {
        this.pdfInitialSize.height = 0;
        this.removeAutoCanvasResize();
      }

      // Hide and disabled pinch zoom on PC View
      if (this.windowWidth > 960) {
        this.isPCView = true;
        const element = this.$refs.pinchZoom;
        if (element && element.isZoomedIn) {
          element.toggleZoom();
        }
      }
    });
    this.setPdfCurrentLesson();
    window.addEventListener('resize', this.onOrientationChangeIpad);
  },
  methods: {
    ...mapMutations('layout', ['materialFullScreen']),
    ...mapMutations('material', ['resetPdfZoom', 'setShowMaterial']),

    ifIosLevel0() {
      const device = new UAParser().getResult();
      // Fix for assessment materials not loading on safari ios
      this.isMobileMacOsSafari =
        this.isMobileWidth && this.isMaterialAssessmentOrLevel0 && device.os.name == 'Mac OS';
      this.isMobileIosSafari =
        this.isMobileWidth && this.isMaterialAssessmentOrLevel0 && device.os.name == 'iOS';
    },

    onOrientationChangeIpad() {
      if (this.isIpadWithMacOsUserAgent()) {
        this.$logger.info(
          'Orientation is changed to ' + (this.isPortraitOrientation() ? 'portrait' : 'landscape'),
          {
            operation: 'device:ipad'
          }
        );
        this.initializePdf = !this.isPortraitOrientation() ? true : false;
      }
    },

    isIpadWithMacOsUserAgent() {
      const ua = new UAParser();
      let isIpad = false;
      if (
        ua.getOS().name === 'Mac OS' &&
        ua.getDevice().model === undefined &&
        /* IPAD Mini 5th Gen, IPAD Air 2 */
        ((screen.width == 768 && screen.height == 1024) ||
          (screen.width == 1024 && screen.height == 768) ||
          /* IPAD Pro 2017 */
          (screen.width == 1112 && screen.height == 834) ||
          (screen.width == 834 && screen.height == 1112) ||
          /* IPAD Air 4th Gen */
          (screen.width == 1180 && screen.height == 820) ||
          (screen.width == 820 && screen.height == 1180) ||
          /* IPAD 8th Gen */
          (screen.width == 1080 && screen.height == 810) ||
          (screen.width == 810 && screen.height == 1080) ||
          /* IPAD Pro 11inches 2nd Gen */
          (screen.width == 834 && screen.height == 1194) ||
          (screen.width == 1194 && screen.height == 834))
      ) {
        isIpad = true;
        this.$logger.info('User is using an ipad with Mac OS user agent', {
          operation: 'device:ipad'
        });
      }

      return isIpad;
    },

    isIpadMini() {
      const ua = new UAParser();
      const device = ua.getDevice().model;
      if (
        ((screen.width == 768 && screen.height == 1024) ||
          (screen.width == 1024 && screen.height == 768)) &&
        device === 'iPad'
      ) {
        return true;
      }

      return false;
    },

    isPortraitOrientation() {
      const isPortrait = window.innerHeight > window.innerWidth;
      this.$logger.info('Device orientation is ' + (isPortrait ? 'portrait' : 'landscape'), {
        operation: 'device:ipad'
      });

      return isPortrait;
    },

    setInitializePdfForIpad() {
      if (
        !this.showMaterial &&
        (this.isIpadWithMacOsUserAgent() || this.isIpadMini()) &&
        !this.isPortraitOrientation()
      ) {
        this.$logger.info(
          'After 5 seconds, PDF material is still disabled, set initializePdf from ' +
            this.initializePdf +
            ' to true',
          {
            operation: 'device:ipad'
          }
        );
        this.initializePdf = true;
      }
    },

    isToEnableDisplayOfPdf() {
      this.isToEnablePdf = true;
    },

    fullScreenMaterial() {
      this.materialFullScreen();
    },

    /**
     * Temporary Validation if given file is pdf
     */
    validatePDF() {
      return this.pdfUrl.indexOf('.pdf') == this.pdfUrl.length - 4;
    },

    /**
     * Load clicked link in new tab
     */
    pdfLinkInNewTab() {
      document.querySelector('.annotationLayer').onclick = (e) => {
        e.preventDefault();
        if (e.target.href) {
          window.open(e.target.href, '_blank');
        }
      };
    },

    onPdfLoaded(evt) {
      this.pdfLoaded = evt;
      this.removeAutoCanvasResize();
    },

    /**
     * Add short delay to get the newest canvas size
     */
    removeAutoCanvasResize() {
      setTimeout(() => {
        this.setPDFSize();
      }, 200);
    },

    /**
     * Set PDF current lesson page for trainer
     */
    setPdfCurrentLesson() {
      if (this.isTrainer) {
        this.pdfCurrentPage =
          this.isAssessmentLesson || this.isDefaultLayout ? 1 : this.currentLesson + 1;
      }
    },

    /*
     * Manually set the pdf size to prevent the image displacement
     */
    setPDFSize() {
      const pdfCanvas = document.querySelector('.pdf canvas');
      if (!pdfCanvas) return;

      /** Canvas limit on mobile devices */
      /* Fix for the canvas limit error, but needs more testing and investigation
      const canvasLimit = 5242880;
      const currentDimension = parseInt(pdfCanvas.width) * parseInt(pdfCanvas.height);
      if (this.isMobileWidth && this.isMobileIosSafari && this.isMaterialAssessmentOrLevel0 && (currentDimension >= canvasLimit)) {
        const scalar = Math.sqrt(canvasLimit) / Math.sqrt(currentDimension);
        pdfCanvas.width = Math.floor(pdfCanvas.width * scalar);
        pdfCanvas.height = Math.floor(pdfCanvas.height * scalar);
      }
      */

      if (!this.pdfInitialSize.height && this.pdfLoaded) {
        // Assessment PC set the canvas height on change of lesson
        let pdfCanvasHeight = pdfCanvas.height;
        if (this.isMaterialAssessmentOrLevel0 && this.windowWidth >= 960) {
          pdfCanvasHeight = parseInt(pdfCanvas.height) / (this.pdfZoom / 100);
        }
        this.pdfInitialSize.height =
          (parseInt(pdfCanvas.offsetWidth) * pdfCanvasHeight) / pdfCanvas.width;
        this.pdfInitialSize.width = parseInt(pdfCanvas.offsetWidth);
      }

      // set pdf size based on the initial dimension and current zoom level
      if (this.pdfLoaded && this.isMaterialAssessmentOrLevel0 && this.isMobileWidth) {
        pdfCanvas.style.height =
          (parseInt(pdfCanvas.offsetWidth) * pdfCanvas.height) / pdfCanvas.width + 'px';
      } else {
        pdfCanvas.style.height = this.pdfInitialSize.height * (this.pdfZoom / 100) + 'px';
      }

      // set annotation scale (link)
      const pdfLink = document.querySelector('.annotationLayer');
      const scale = parseInt(pdfCanvas.style.height) / pdfCanvas.height;
      pdfLink.style.transform = `scale(${scale})`;
    },

    /*
     * Get the pdf size ratio from the initial set size
     */
    getPDFRatioSize() {
      const initialWidth = this.pdfInitialSize.width;
      if (!initialWidth) {
        return;
      }
      if (this.isMobileWidth) {
        this.pdfInitialSize.width = window.innerWidth;
        this.pdfInitialSize.height =
          (window.innerWidth * this.pdfInitialSize.height) / initialWidth;
      } else {
        const pdfWrapper = document.querySelector('.pdf-wrapper');
        this.pdfInitialSize.width = pdfWrapper?.offsetWidth;
        this.pdfInitialSize.height =
          (this.pdfInitialSize.width * this.pdfInitialSize.height) / initialWidth;
      }
      this.removeAutoCanvasResize();
    },

    /*
     * Set scroll mirror for assessment lesson only
     */
    scrollMaterial() {
      if (this.isAssessmentLesson && !this.showPDFHtml) {
        const scrollBar = this.$refs['pdf-scrollbar'];
        const scrollX = scrollBar.$el.scrollTop;
        const scrollY = scrollBar.$el.scrollLeft;
        const pdfCanvasHeight = document.querySelector('.pdf-wrapper canvas').style.height;
        const percentageHeight = scrollX / parseInt(pdfCanvasHeight, 10);
        this.socket.emit('signal', {
          type: 'pilot',
          scrollPoint: { x: percentageHeight, y: scrollY }
        });
      }
    },

    /**
     * Load initial width of assessment/level0 canvas
     */
    loadMaterialInitialWidth() {
      const pdfCanvas = document.querySelector('.pdf canvas');
      if (this.isAssessmentLesson && this.pdfZoom > 160) {
        pdfCanvas.style.width = '1015px';
      }
    },
    resetPdfZoomLevel0() {
      if (this.isLevel0 && this.pdfZoom !== 100) {
        this.resetPdfZoom();
      }
    }
  }
};
</script>

<style scoped src="@/assets/css/pc/pdf_material.css"></style>
